import {Box, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, Tooltip, Typography, useTheme} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {PerformanceData} from "../Performance/PerformanceChartReducer";
import {useAppSelector} from "../../../Config/Hooks";
import {RootState} from "../../../Config/Store";
import useInterval from "../../../Shared/Hooks/useInterval";
import {
    ACard,
    ASectionTitle,
    AMetricCard,
    AMultiSelectFilter,
    AMultiSelectOption,
    AMultiSelectOptionId,
    Skeleton,
} from "@atiautomacao/ati-ui-library";
import {MenuItemStatus} from "../../../Shared/Components/Layout/menu/types/MenuItemStatus";
import {truncateNumber} from "../../../Utils/NumberUtil";
import DataNotFound from "../../../Shared/Components/DataNotFoundMessage";
import {formatAvailability, formatPerformanceRatio, getPerformanceRatioTone} from "../../../Utils/PerformanceRatioUtils";
import {pillColors} from "../Unavailability/Pill";
import {QueryClient, QueryClientProvider} from "react-query";
import PerformanceTotalizers from "../Performance/components/PerformanceTotalizers";
import {
    buildPerformanceTotalizersQueryKey,
    markPerformanceTotalizersAutomaticRefetch,
    PERFORMANCE_TOTALIZERS_QUERY_KEY_ROOT,
    releasePerformanceTotalizersAutomaticRefetchMarker,
    serializePerformanceTotalizersQueryKey,
} from "../Performance/components/reactQuery/PerformanceTotalizersReactQuery";
import {
    applyPerformanceBatch,
    fetchPerformanceBatch,
    markPerformanceCardsAsError,
    PERFORMANCE_ERROR_MESSAGE,
    PERFORMANCE_RETRY_DELAY_MS,
} from "../Performance/PerformanceBatch";
import {useSnackbar} from "notistack";
import {
    calculateProgress,
    formatValueToMega,
    hasExpectedData,
    NO_DATA,
    NO_DATA_PERCENTAGE,
} from "../Performance/utils/PerformanceDisplayUtils";
import {LocalDateTimeFormatISO} from "../../../Utils/DateFormatPatternUtils";
import {usePersistedSelection} from "../../../Shared/Hooks/usePersistedSelection";
import {PERFORMANCE_CARD_SORT_CRITERION_STORAGE_KEY, POWER_STATION_IDS_FILTER_STORAGE_KEY} from "../../../Config/Constants";
import themeDefaults from "../../../Shared/Theme/ThemeDefaults";
import {
    DEFAULT_PERFORMANCE_SORT_CRITERION,
    PERFORMANCE_SORT_OPTIONS,
    PerformanceSortCriterion,
    areIdOrdersEqual,
    parsePerformanceSortCriterion,
    projectCardsByCommittedOrder,
    reconcileCommittedCardOrder,
    sortPerformanceCards,
} from "../Performance/utils/PerformanceCardSort";

const readStoredSortCriterion = (): PerformanceSortCriterion => {
    try {
        return parsePerformanceSortCriterion(
            window.localStorage.getItem(PERFORMANCE_CARD_SORT_CRITERION_STORAGE_KEY)
        ) ?? DEFAULT_PERFORMANCE_SORT_CRITERION;
    } catch {
        return DEFAULT_PERFORMANCE_SORT_CRITERION;
    }
};

const persistSortCriterion = (criterion: PerformanceSortCriterion) => {
    try {
        window.localStorage.setItem(PERFORMANCE_CARD_SORT_CRITERION_STORAGE_KEY, criterion);
    } catch {
        /* storage indisponível ou cheio */
    }
};

const createPlaceholderCard = (ps: {id: number; name: string}): PerformanceData => ({
    id: ps.id,
    powerStationName: ps.name,
    etsStatus: "Ignored" as MenuItemStatus,
    activePower: 0,
    generationRate: 0,
    irradiance: 0,
    generatedEnergy: 0,
    expectedIrradiance: 0,
    expectedInjectedEnergy: 0,
    performanceRatio: 0,
    availability: 0,
    loading: true,
});

export default function DashboardOverviewPerformancePage() {
    const theme = useTheme();
    const queryClient = useMemo(() => new QueryClient({
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: false,
            },
        },
    }), []);
    const {enqueueSnackbar} = useSnackbar();
    const {periodSynoptic} = useAppSelector((state: RootState) => state.performanceChart);
    const entitiesDashboard = useAppSelector(
        (state: RootState) => state.dashboardNavigationMenu.entities
    );
    const [cardRequestPending, setCardRequestPending] = useState(false);
    const [powerStationList, setPowerStationList] = useState<Array<PerformanceData>>([]);
    const [sortCriterion, setSortCriterion] = useState<PerformanceSortCriterion>(readStoredSortCriterion);
    const [committedCardOrder, setCommittedCardOrder] = useState<number[]>([]);
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    const prevPeriodKeyRef = useRef<string>("");
    const automaticCycleBusyRef = useRef(false);
    const totalizersRefetchSettledRef = useRef(true);
    const cardRequestDoneRef = useRef(true);
    // Identifica a requisição vigente: respostas de contexto anterior (período/filtro) são ignoradas.
    const cardRequestTokenRef = useRef(0);

    const powerStationOptions = useMemo<AMultiSelectOption[]>(
        () => [...(entitiesDashboard ?? [])]
            .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
            .map((e) => ({id: e.id, label: e.name})),
        [entitiesDashboard]
    );

    const powerStationOptionIds = useMemo<AMultiSelectOptionId[]>(
        () => powerStationOptions.map((o) => o.id),
        [powerStationOptions]
    );

    const {
        selectedIds: selectedPowerStationIds,
        setSelectedIds: setSelectedPowerStationIds,
        isInitialized,
    } = usePersistedSelection<AMultiSelectOptionId>({
        storageKey: POWER_STATION_IDS_FILTER_STORAGE_KEY,
        allOptionIds: powerStationOptionIds,
    });

    const handleApplyPowerStationFilter = useCallback(
        (ids: AMultiSelectOptionId[]) => {
            if (ids.length === 0) return;
            setSelectedPowerStationIds(ids);
        },
        [setSelectedPowerStationIds]
    );

    const filteredEntities = useMemo(() => {
        if (!entitiesDashboard) return [];
        if (!isInitialized) return entitiesDashboard;
        const selectedSet = new Set(selectedPowerStationIds.map((id) => Number(id)));
        return entitiesDashboard.filter((e) => selectedSet.has(e.id));
    }, [isInitialized, selectedPowerStationIds, entitiesDashboard]);

    const filteredPowerStationIds = useMemo(
        () => filteredEntities.map((e) => e.id),
        [filteredEntities]
    );

    const filteredIdsKey = useMemo(
        () => filteredPowerStationIds.join(","),
        [filteredPowerStationIds]
    );

    const isPeriodValid = !!periodSynoptic && !periodSynoptic.fromDateTime.toString().includes("999Z");

    const periodKey = useMemo(() => {
        if (!periodSynoptic) return "";
        return `${periodSynoptic.fromDateTime}-${periodSynoptic.toDateTime}`;
    }, [periodSynoptic]);

    // RF02: com todas as usinas do usuário selecionadas, a lista de ids é omitida.
    const allPowerStationsSelected = powerStationOptionIds.length > 0
        && filteredPowerStationIds.length === powerStationOptionIds.length;

    const tryFinishAutomaticCycle = useCallback(() => {
        if (!automaticCycleBusyRef.current) {
            return;
        }
        if (!cardRequestDoneRef.current || !totalizersRefetchSettledRef.current) {
            return;
        }
        automaticCycleBusyRef.current = false;
    }, []);

    /**
     * SGD-3904: uma única requisição para todas as usinas selecionadas (RF01). Com 4 falhas seguidas
     * (RN08) todos os cards ficam "dados não encontrados" e a tela notifica o erro.
     */
    const loadCards = useCallback(() => {
        cardRequestTokenRef.current += 1;
        const requestToken = cardRequestTokenRef.current;
        cardRequestDoneRef.current = false;
        setCardRequestPending(true);

        const isCurrentRequest = () => requestToken === cardRequestTokenRef.current;
        const finishRequest = () => {
            cardRequestDoneRef.current = true;
            setCardRequestPending(false);
            tryFinishAutomaticCycle();
        };

        fetchPerformanceBatch(
            LocalDateTimeFormatISO(periodSynoptic.fromDateTime),
            LocalDateTimeFormatISO(periodSynoptic.toDateTime),
            allPowerStationsSelected ? undefined : filteredPowerStationIds,
            PERFORMANCE_RETRY_DELAY_MS
        )
            .then((response) => {
                if (!isCurrentRequest()) {
                    return;
                }
                setPowerStationList((prevList) => applyPerformanceBatch(prevList, response));
                finishRequest();
            })
            .catch(() => {
                if (!isCurrentRequest()) {
                    return;
                }
                setPowerStationList((prevList) => markPerformanceCardsAsError(prevList));
                enqueueSnackbar(PERFORMANCE_ERROR_MESSAGE, {variant: "error"});
                finishRequest();
            });
    }, [periodSynoptic, allPowerStationsSelected, filteredPowerStationIds, enqueueSnackbar, tryFinishAutomaticCycle]);

    useEffect(() => {
        if (!isPeriodValid || filteredEntities.length === 0) {
            // RN09: sem usina selecionada não há requisição; descarta a que estiver em andamento.
            cardRequestTokenRef.current += 1;
            setPowerStationList([]);
            setCommittedCardOrder([]);
            setCardRequestPending(false);
            prevPeriodKeyRef.current = periodKey;
            cardRequestDoneRef.current = true;
            automaticCycleBusyRef.current = false;
            return;
        }

        const periodChanged = prevPeriodKeyRef.current !== periodKey;
        prevPeriodKeyRef.current = periodKey;

        automaticCycleBusyRef.current = false;
        totalizersRefetchSettledRef.current = true;

        setPowerStationList((prev) => {
            const prevById = new Map(prev.map((item) => [item.id, item]));
            return filteredEntities.map((ps) => {
                const existing = prevById.get(ps.id);
                // RN07: troca só de filtro mantém os cards que continuam; novas usinas carregam.
                // RN06: troca de período volta todos os cards ao carregamento.
                if (!periodChanged && existing) {
                    return existing;
                }
                return createPlaceholderCard(ps);
            });
        });
        setCommittedCardOrder((prev) =>
            reconcileCommittedCardOrder(prev, filteredEntities.map((entity) => entity.id))
        );
        loadCards();
        // loadCards lê o período e o filtro do mesmo render; o efeito roda só quando eles mudam.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodKey, filteredIdsKey, isPeriodValid, filteredEntities]);

    useInterval(() => {
        if (filteredEntities.length === 0 || !isPeriodValid) {
            return;
        }
        if (automaticCycleBusyRef.current) {
            return;
        }
        if (!cardRequestDoneRef.current) {
            return;
        }
        if (
            queryClient.isFetching({
                predicate: (query) => query.queryKey[0] === PERFORMANCE_TOTALIZERS_QUERY_KEY_ROOT,
            })
        ) {
            return;
        }

        automaticCycleBusyRef.current = true;
        totalizersRefetchSettledRef.current = false;

        markPerformanceTotalizersAutomaticRefetch(
            filteredPowerStationIds,
            LocalDateTimeFormatISO(periodSynoptic.fromDateTime),
            LocalDateTimeFormatISO(periodSynoptic.toDateTime)
        );

        const totalizersQueryKey = buildPerformanceTotalizersQueryKey(
            filteredPowerStationIds,
            LocalDateTimeFormatISO(periodSynoptic.fromDateTime),
            LocalDateTimeFormatISO(periodSynoptic.toDateTime)
        );
        const hadMatchingTotalizersQuery = queryClient
            .getQueryCache()
            .findAll({queryKey: totalizersQueryKey, exact: true})
            .some((cachedQuery) => cachedQuery.getObserversCount() > 0);

        void queryClient
            .refetchQueries({
                queryKey: totalizersQueryKey,
            })
            .finally(() => {
                releasePerformanceTotalizersAutomaticRefetchMarker(
                    filteredPowerStationIds,
                    LocalDateTimeFormatISO(periodSynoptic.fromDateTime),
                    LocalDateTimeFormatISO(periodSynoptic.toDateTime),
                    hadMatchingTotalizersQuery
                );
                totalizersRefetchSettledRef.current = true;
                tryFinishAutomaticCycle();
            });

        // RN05: os cards mantêm os dados atuais até a resposta e atualizam todos de uma vez.
        loadCards();
    }, userConfig.mainUpdateInterval);

    useEffect(() => {
        if (powerStationList.length === 0) {
            return;
        }
        if (cardRequestPending || powerStationList.some((item) => item.loading === true)) {
            return;
        }
        const nextOrder = sortPerformanceCards(powerStationList, sortCriterion).map((item) => item.id);
        setCommittedCardOrder((prev) => (areIdOrdersEqual(prev, nextOrder) ? prev : nextOrder));
    }, [cardRequestPending, powerStationList, sortCriterion]);

    const handleSortCriterionChange = (event: SelectChangeEvent<string>) => {
        const nextCriterion = parsePerformanceSortCriterion(event.target.value);
        if (!nextCriterion) {
            return;
        }
        setSortCriterion(nextCriterion);
        persistSortCriterion(nextCriterion);
    };

    const metricLabelSx = {
        fontFamily: theme.typography.fontFamily,
        fontWeight: 400,
        fontSize: "12px",
        lineHeight: "15px",
        letterSpacing: "0.2px",
    };

    /**
     * SGD-3680: apenas a ausência de dado esperado é exibida como "-" e "- %". Um valor
     * esperado igual a zero continua sendo exibido como 0, mas sem percentual, porque a
     * razão medido/esperado não é calculável.
     */
    const buildComparisonMetric = (
        label: string,
        measuredValue: number,
        expectedValue: number,
        color: string
    ) => {
        const progress = calculateProgress(measuredValue, expectedValue);

        return {
            label: {label, sx: metricLabelSx},
            value: `${formatValueToMega(measuredValue)} (${progress != null ? `${progress}%` : NO_DATA_PERCENTAGE})`,
            secondaryValue: hasExpectedData(expectedValue) ? formatValueToMega(expectedValue) : NO_DATA,
            // 0 e não undefined: o AMetricCard omite a barra quando progress é nulo,
            // o que alteraria a altura do card.
            progress: progress ?? 0,
            valueColor: color,
            barColor: color,
        };
    };

    const displayedPowerStationList = useMemo(() => {
        const projectedCards = projectCardsByCommittedOrder(powerStationList, committedCardOrder);
        return [
            ...projectedCards.filter((item) => item.loading !== true),
            ...projectedCards.filter((item) => item.loading === true),
        ];
    }, [committedCardOrder, powerStationList]);

    return (
        <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", paddingLeft: 2, paddingRight: 2}}>
            <ASectionTitle
                styleContainer={{padding: "32px 24px 24px 24px"}}
                titleLabel="Performance"
                contentChildren={
                    <Stack
                        direction="row"
                        sx={{
                            width: {xs: "100%", sm: "auto"},
                            minWidth: 0,
                            flexShrink: 1,
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                        }}
                    >
                        <AMultiSelectFilter
                            options={powerStationOptions}
                            selectedIds={selectedPowerStationIds}
                            onApply={handleApplyPowerStationFilter}
                            triggerLabel="Usinas"
                            searchPlaceholder="Buscar usina..."
                        />
                        <FormControl
                            size="small"
                            variant="outlined"
                            sx={{
                                minWidth: 135,
                                flex: {xs: "1 1 135px", sm: "0 0 auto"},
                            }}
                        >
                            <InputLabel id="performance-card-sort-label">Ordenar por</InputLabel>
                            <Select
                                labelId="performance-card-sort-label"
                                id="performance-card-sort"
                                value={sortCriterion}
                                label="Ordenar por"
                                onChange={handleSortCriterionChange}
                            >
                                {PERFORMANCE_SORT_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                }
            />
            {isPeriodValid && filteredPowerStationIds.length > 0 && (
                <QueryClientProvider client={queryClient}>
                    <PerformanceTotalizers
                        powerStationIds={filteredPowerStationIds}
                        startDateTime={LocalDateTimeFormatISO(periodSynoptic.fromDateTime)}
                        endDateTime={LocalDateTimeFormatISO(periodSynoptic.toDateTime)}
                    />
                </QueryClientProvider>
            )}
            {displayedPowerStationList.length > 0 ? (
                <Grid container sx={{gap: "10px", paddingLeft: 3}}>
                    {displayedPowerStationList.map((data) => {
                        const performanceRatioTone = getPerformanceRatioTone(truncateNumber(data.performanceRatio, 1));

                        const performanceRatioPalette = pillColors[performanceRatioTone][theme.palette.mode === 'light' ? 'light' : 'dark'];
                        const performanceRatioColor = performanceRatioPalette.fg;
                        const performanceRatioBackground = performanceRatioPalette.bg;

                        if (data.loading) {
                            return (
                                <Grid item key={data.id}>
                                    <Box
                                        sx={{
                                            backgroundColor: "transparent",
                                            backgroundImage: "none",
                                            boxShadow: "none",
                                            border: "none",
                                            borderRadius: 0,
                                            padding: 0,
                                            width: "179.22px",
                                            height: "150px",
                                        }}
                                    >
                                        <Skeleton animation="wave" height={234} width="100%" variant="rectangular"/>
                                    </Box>
                                </Grid>
                            );
                        }
                        // RN08: falha da consulta -> "dados não encontrados". RN04: usina sem retorno ->
                        // "sem dados", no mesmo card, sem o ícone de alerta (estado neutro).
                        if (data.error || data.noData) {
                            return (
                                <Grid item key={data.id}>
                                    <Box
                                        sx={{
                                            border: "0.5px solid",
                                            borderColor: "divider",
                                            display: "flex",
                                            flexDirection: "column",
                                            width: "179.22px",
                                            height: "234px",
                                            gap: "12px",
                                            opacity: 1,
                                            borderRadius: 2,
                                            bgcolor: theme.palette.mode === 'dark' ? themeDefaults.backgroundCard.dark : undefined,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                padding: "16px",
                                            }}
                                        >
                                            <Tooltip title={data.powerStationName} arrow placement="top">
                                                <Typography
                                                    sx={{
                                                        fontFamily: "Work Sans",
                                                        fontWeight: 600,
                                                        fontSize: "14px",
                                                        lineHeight: "16px",
                                                    }}
                                                    aria-placeholder={data.powerStationName}
                                                >
                                                    {data.powerStationName.length > 15
                                                        ? data.powerStationName.substring(0, 15) + "..."
                                                        : data.powerStationName}
                                                </Typography>

                                            </Tooltip>
                                          
                                        </Box>

                                        {data.noData
                                            ? <DataNotFound message="Sem dados" showIcon={false}/>
                                            : <DataNotFound/>}
                                    </Box>
                                </Grid>
                            );
                        }
                        return (
                            <Grid item key={data.id}>
                                <AMetricCard
                                    sx={{backgroundColor: theme.palette.mode === 'dark' ? themeDefaults.backgroundCard.dark : undefined}}
                                    title={
                                        data.powerStationName.length > 15
                                            ? data.powerStationName.substring(0, 15) + "..."
                                            : data.powerStationName
                                    }
                                    metrics={[
                                        {
                                            label: {label: "PR"},
                                            value: `${formatPerformanceRatio(truncateNumber(data.performanceRatio, 2))}%`,
                                            variant: "chip",
                                            valueColor: performanceRatioColor,
                                            barColor: performanceRatioBackground,
                                            sx: {justifyContent: "center", alignItems: "center"},
                                        },
                                        buildComparisonMetric(
                                            "Energia gerada (MWh)",
                                            data.generatedEnergy,
                                            data.expectedInjectedEnergy,
                                            performanceRatioColor
                                        ),
                                        buildComparisonMetric(
                                            "Irradiação (kWh/m²)",
                                            data.irradiance,
                                            data.expectedIrradiance,
                                            performanceRatioColor
                                        ),
                                        {
                                            label: {
                                                label: "Disponibilidade",
                                                sx: {
                                                    fontFamily: theme.typography.fontFamily,
                                                    fontWeight: 400,
                                                    fontSize: "12px",
                                                    color: "text.secondary",
                                                },
                                            },
                                            variant: "inline",
                                            value: `${formatAvailability(data.availability)}%`,
                                            valueColor: theme.palette.text.secondary,
                                        },
                                    ]}
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            ) : (
                <Grid container spacing={1}>
                    <ACard>Dados não encontrados</ACard>
                </Grid>
            )}
        </Box>
    );
}
